import React, { useContext, useState } from 'react';
import { WindowSizeContext } from '/src/App';
import { CurlyBraceDivider } from '/src/Components/CurlyBraceDivider/CurlyBraceDivider';
import GaugeChart from 'react-gauge-chart';
import ProjectCardSubSectionHeader from '../ProjectCardSubSectionHeader/ProjectCardSubSectionHeader';
import { CirclePlus } from 'lucide-react';
import { Doughnut, Line } from 'react-chartjs-2';

import './style.css';


const capitalFreedInfo = {
    label: 'Sudden Costs Avoided',
    color: '#7c1158'
}

const opexInfo = {
    label: 'Lower Recurring Expenses',
    color: '#0d88e6'
}

const propertyValueUpsideInfo = {
    label: 'Property Value Upside',
    color: '#87bc45'
}



// THINGS FOR THE CHARTS
const valueStreamConfig = {
    capital_freed: capitalFreedInfo,
    opex: opexInfo,
    property_value_upside: propertyValueUpsideInfo
};

// Legend (For mobile)
const legendItems = [
    capitalFreedInfo,
    opexInfo,
    propertyValueUpsideInfo
]



// Create the gradient effects in the render
const createGradient = (ctx, color) => {
    const gradient = ctx.createLinearGradient(0, 0, 0, 400);
    gradient.addColorStop(0, adjustColor(color, 30));  // Lighter at top
    gradient.addColorStop(0.5, color);                 // Original color in middle
    gradient.addColorStop(1, adjustColor(color, -30)); // Darker at bottom
    return gradient;
};

// Helper function to darken/lighten colors
const adjustColor = (color, amount) => {
    const clamp = (val) => Math.min(255, Math.max(0, val));

    // Convert hex to RGB
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Adjust each channel
    const adjustR = clamp(r + amount);
    const adjustG = clamp(g + amount);
    const adjustB = clamp(b + amount);

    // Convert back to hex
    return `#${adjustR.toString(16).padStart(2, '0')}${adjustG.toString(16).padStart(2, '0')}${adjustB.toString(16).padStart(2, '0')}`;
};


// FOR THE GAUGES
const getDifficultyLabel = (value) => {
    if (value <= 0.33) return "Low";
    if (value <= 0.66) return "Medium";
    return "High";
};

const ProjectCardCosts = ({ project }) => {
    const { deviceType } = useContext(WindowSizeContext);

    const [showROIChart, setShowROIChart] = useState(false);

    const COST_SECTION_HEADER = "Project Cost Considerations";
    const COSTS_HEADER = "Costs";
    const COSTS_INTRO = "It is important to consider costs beyond just financial, as any way done will have an impact on your life and the home's useability."
    const BENEFITS_HEADER = "Payoff";
    const BENEFITS_INTRO = "This project benefits you across a number of different value streams. You may choose to value some over others, but now you have a complete picture!"

    // FOR THE COSTS SIDE
    const gaugeData = [
        {
            name: "Project Complexity",
            value: project.project_complexity,
            label: getDifficultyLabel(project.project_complexity)
        },
        {
            name: "Project Disruption",
            value: project.project_disruption,
            label: getDifficultyLabel(project.project_disruption)
        }
    ];
    const projectCostMaterials = project.project_cost_materials;
    const projectCostLabor = project.project_cost_labor;
    const projectCostTotal = projectCostMaterials + projectCostLabor;
    const projectPercentageMaterials = (projectCostMaterials / projectCostTotal) * 100;
    const projectPercentageLabor = (projectCostLabor / projectCostTotal) * 100;

    // FOR THE BENEFITS SIDE
    const financials = project.project_payoff_by_scenario[0]

    // Calculate totals
    const capitalFreedTotal = financials.by_value_type.capital_freed.total;

    // Sum property value upside
    const propertyValueTotal = financials.by_value_type.property_value_upside.by_year.reduce(
        (sum, value) => sum + value, 0
    );

    // Calculate opex totals - only include non-zero values
    const opexCategories = Object.entries(financials.by_value_type.opex)
        .map(([key, value]) => ({
            name: key.charAt(0).toUpperCase() + key.slice(1),
            value: value.total
        }))
        .filter(category => category.value > 0);

    const opexTotal = opexCategories.reduce((sum, category) => sum + category.value, 0);

    const breakeven_year = financials.roi_assessment.all_value_streams.breakeven_year

    // Create arrays for chart data, only including non-zero values
    const chartData = [
        ...(capitalFreedTotal > 0 ? [{
            label: valueStreamConfig.capital_freed.label,
            value: capitalFreedTotal,
            color: valueStreamConfig.capital_freed.color
        }] : []),
        ...(propertyValueTotal > 0 ? [{
            label: valueStreamConfig.property_value_upside.label,
            value: propertyValueTotal,
            color: valueStreamConfig.property_value_upside.color
        }] : []),
        ...(opexTotal > 0 ? [{
            label: valueStreamConfig.opex.label,
            value: opexTotal,
            color: valueStreamConfig.opex.color,
            breakdown: opexCategories
        }] : [])
    ];

    // Calculate total value from the filtered data
    const totalValue = chartData.reduce((sum, item) => sum + item.value, 0);

    // DOUGHTNUT OPTIONS
    const doughtnutOptions = {
        responsive: true,
        maintainAspectRatio: true,
        aspectRatio: 2,
        plugins: {
            legend: {
                display: deviceType !== 'mobile',
                position: 'bottom',
                labels: {
                    font: {
                        family: 'inherit'
                    },
                    padding: 20,
                    boxWidth: 15,
                    usePointStyle: true
                },
                maxWidth: '100%',
                align: 'center'
            },
            tooltip: {
                callbacks: {
                    label: (context) => {
                        const dataPoint = chartData[context.dataIndex];
                        if (dataPoint.breakdown) {
                            const lines = [
                                `Total: $${dataPoint.value.toLocaleString()}`,
                                '────────────',
                                ...dataPoint.breakdown.map(item =>
                                    `${item.name}: $${item.value.toLocaleString()}`
                                )
                            ];
                            return lines;
                        }
                        return `$${context.raw.toLocaleString()}`;
                    }
                }
            },
            datalabels: {
                color: '#fff',
                font: {
                    weight: 'bold',
                    size: 16,
                    family: 'inherit'
                },
                formatter: (value, context) => {
                    const percentage = ((value / totalValue) * 100).toFixed(0);
                    return `${percentage}%`;
                }
            }
        },
        cutout: '60%'
    };

    const doughnutData = {
        labels: chartData.map(item => item.label),
        datasets: [{
            data: chartData.map(item => item.value),
            backgroundColor: (context) => {
                const chart = context.chart;
                const { ctx, chartArea } = chart;

                if (!chartArea) {
                    return;
                }

                // Get the color for this segment
                const segmentColor = chartData[context.dataIndex].color;
                return createGradient(ctx, segmentColor);
            },
            borderWidth: 0,
            hoverOffset: 4
        }]
    };
    console.log(doughnutData);

    const years = Array.from({ length: 10 }, (_, i) => `Year ${i + 1}`);

    const lineChartData = {
        labels: years,
        datasets: [
            {
                label: valueStreamConfig.capital_freed.label,
                data: financials.roi_assessment.capital_freed.by_year[9] > 0 ? financials.roi_assessment.capital_freed.by_year : [],
                borderColor: valueStreamConfig.capital_freed.color,
                backgroundColor: valueStreamConfig.capital_freed.color,
                fill: 'origin',
                tension: 0.4,
                pointRadius: 0
            },
            {
                label: valueStreamConfig.opex.label,
                data: financials.roi_assessment.opex.by_year[9] > 0 ?
                    financials.roi_assessment.opex.by_year.map((value, index) =>
                        value + financials.roi_assessment.capital_freed.by_year[index]
                    ) : [],
                borderColor: valueStreamConfig.opex.color,
                backgroundColor: valueStreamConfig.opex.color,
                fill: true,
                tension: 0.4,
                pointRadius: 0
            },
            {
                label: valueStreamConfig.property_value_upside.label,
                data: financials.roi_assessment.property_value_upside.by_year[9] > 0 ?
                    financials.roi_assessment.property_value_upside.by_year.map((value, index) =>
                        value + financials.roi_assessment.opex.by_year[index] + financials.roi_assessment.capital_freed.by_year[index]
                    ) : [],
                borderColor: valueStreamConfig.property_value_upside.color,
                backgroundColor: valueStreamConfig.property_value_upside.color,
                fill: true,
                tension: 0.4,
                pointRadius: 0
            }
        ].filter(dataset => dataset.data.length > 0)
    };

    const lineChartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        aspectRatio: 1,
        plugins: {
            tooltip: {
                mode: 'index',
                intersect: false,
                callbacks: {
                    title: (context) => `Year ${context[0].dataIndex + 1}`,
                    label: (context) => {
                        const index = context.dataIndex;
                        let value;

                        switch (context.dataset.label) {
                            case 'Capital Freed':
                                value = financials.roi_assessment.capital_freed.by_year[index];
                                break;
                            case 'Operating Expenses':
                                value = financials.roi_assessment.opex.by_year[index];
                                break;
                            case 'Property Value Upside':
                                value = financials.roi_assessment.property_value_upside.by_year[index];
                                break;
                        }

                        if (value > 0) {
                            return `${context.dataset.label}: ${value.toFixed(1)}x`;
                        }
                        return null;
                    }
                }
            },
            filler: {
                propagate: true
            },
            legend: {
                position: 'bottom',
                labels: {
                    font: {
                        family: 'sofia-pro, sans-serif'
                    },
                    padding: 20,
                    usePointStyle: true
                }
            },
            datalabels: {
                display: false
            }
        },
        scales: {
            y: {
                stacked: false,
                beginAtZero: true,
                ticks: {
                    callback: value => `${value}x`
                },
                grid: {
                    color: '#E5E5E5'
                }
            },
            x: {
                grid: {
                    display: false
                }
            }
        }
    };




    return (
        <div className="project-card-section-container">
            <h2 className="project-card-section-header box-shadow-standard">{COST_SECTION_HEADER}</h2>


            <div className={`project-card-costs-grid ${deviceType}`}>
                <div className="individual-project-cost-column">

                    <div className="project-card-costs-section-header">
                        <span className="project-card-costs-section-header-text">{COSTS_HEADER}</span>
                    </div>
                    <p className="project-card-costs-intro-text text-align-center">{COSTS_INTRO}</p>

                    <div className={`project-card-costs-container ${deviceType}`}>
                        <div className={`project-card-costs-total-cost ${deviceType}`}>
                            <span className="project-card-costs-total-cost-value">
                                ${projectCostTotal.toLocaleString()}
                            </span>
                            <span className="project-card-costs-total-cost-label">Total Cost</span>
                        </div>

                        <CurlyBraceDivider color="black" direction="right" />

                        <div className="project-card-costs-column">
                            <div className="project-card-costs-individual-cost">
                                <div className="project-card-costs-individual-cost-percentage">
                                    <span className="project-card-costs-individual-cost-percentage-value">
                                        {projectPercentageMaterials.toFixed(0)}%
                                    </span>
                                </div>
                                <div className="project-card-costs-individual-cost-values-column">
                                    <span className="individual-project-cost-value">
                                        ${projectCostMaterials.toLocaleString()}
                                    </span>
                                    <span className="project-card-costs-individual-cost-label">Materials</span>
                                </div>
                            </div>
                            <div className="project-card-costs-individual-cost">
                                <div className="project-card-costs-individual-cost-percentage">
                                    <span className="project-card-costs-individual-cost-percentage-value">
                                        {projectPercentageLabor.toFixed(0)}%
                                    </span>
                                </div>
                                <div className="project-card-costs-individual-cost-values-column">
                                    <span className="individual-project-cost-value">
                                        ${projectCostLabor.toLocaleString()}
                                    </span>
                                    <span className="project-card-costs-individual-cost-label">Labor</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <CirclePlus className="project-card-costs-plus-sign" />
                    <div className={`project-card-costs-gauges-container ${deviceType}`}>
                        {gaugeData.map(({ name, value, label }) => (
                            <div key={name} className="project-card-costs-gauges-container-item">
                                <span className="individual-project-gauge-label">{name}</span>
                                <GaugeChart
                                    id={`${name.toLowerCase().replace(' ', '-')}-gauge`}
                                    nrOfLevels={9}
                                    colors={["#34C759", "#FFD60A", "#FF3B30"]}
                                    arcWidth={0.25}
                                    percent={value}
                                    textColor="transparent"
                                    needleColor="#666666"
                                    needleBaseColor="#666666"
                                    className="individual-project-gauge"
                                />
                                <span className="individual-project-gauge-value">{label}</span>
                            </div>
                        ))}
                    </div>

                </div>
                <div className="individual-project-cost-column">
                    <div className="project-card-costs-section-header">
                        <span className="project-card-costs-section-header-text">{BENEFITS_HEADER}</span>
                    </div>
                    <p className="project-card-costs-intro-text text-align-center">{BENEFITS_INTRO}</p>

                    <div className="project-card-benefits-container">
                        <div className={`project-card-benefits-total-value-container ${deviceType}`}>
                            <div className={`project-card-benefits-total-value-item-container ${deviceType}`}>
                                <span className="project-card-benefits-total-value-value-text">
                                    ${totalValue.toLocaleString()}
                                </span>
                                <span className="project-card-benefits-total-value-value-label">
                                    Value Over 10 Years
                                </span>
                            </div>
                            <div className="project-card-benefits-total-value-item-container">
                                <span className="project-card-benefits-total-value-value-text">
                                    {breakeven_year}
                                </span>
                                <span className="project-card-benefits-total-value-value-label">
                                    Years to Break Even
                                </span>
                            </div>
                        </div>
                        <CurlyBraceDivider color="black" />
                        <div className={`project-card-benefits-doughtnut-chart-container ${deviceType}`}>
                            <span className="project-card-benefits-doughtnut-chart-container-text">
                                What is driving that payback?
                            </span>
                            <div className="payoff-chart-container">
                                <Doughnut data={doughnutData} options={doughtnutOptions} />
                            </div>
                            {deviceType === 'mobile' && (
                                <div className="payoff-chart-mobile-legend">
                                    {legendItems.map((item, index) => (
                                        <div key={index} className="payoff-chart-legend-item">
                                            <div className="payoff-chart-legend-item-color" style={{ backgroundColor: item.color }} />
                                            <span className="payoff-chart-legend-item-label">{item.label}</span>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <button className="project-card-benefits-see-roi-chart-button box-shadow-light" onClick={() => setShowROIChart(!showROIChart)}>
                            {showROIChart ? "Hide Chart" : "See Timeline of Return on This Investment"}
                        </button>
                        {showROIChart && (
                            <div className="project-card-benefits-roi-chart-container">
                                <Line data={lineChartData} options={lineChartOptions} />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ProjectCardCosts;
